
import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { onAuthStateChanged } from 'firebase/auth';
import App from './App';
import { auth } from './services/firebase';
import { getTrustedPhone } from './services/device';
import {
  clearSentryRuntimePhone,
  initializeSentry,
  setSentryAuthUser,
  setSentryRuntimePhone,
} from './services/sentry';

initializeSentry();

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error("Could not find root element to mount to");
}

setSentryRuntimePhone(getTrustedPhone());

onAuthStateChanged(auth, (user) => {
  setSentryAuthUser(user ? { id: user.uid, phoneNumber: user.phoneNumber } : null);
  if (!user) {
    setSentryRuntimePhone(getTrustedPhone());
  }
});

window.addEventListener('storage', (event) => {
  if (event.key !== 'clero_trusted_phone') return;
  if (event.newValue) {
    setSentryRuntimePhone(event.newValue);
    return;
  }
  clearSentryRuntimePhone();
});

const root = ReactDOM.createRoot(rootElement);
root.render(
  <React.StrictMode>
    <SentryAppBoundary />
  </React.StrictMode>
);

function SentryAppBoundary() {
  return (
    <ErrorBoundaryFallback>
      <App />
    </ErrorBoundaryFallback>
  );
}

function ErrorBoundaryFallback({ children }: { children: React.ReactNode }) {
  return (
    <Sentry.ErrorBoundary
      fallback={(
        <div className="min-h-screen bg-white text-black flex items-center justify-center px-6 text-center">
          <div className="space-y-3">
            <p className="text-sm font-bold uppercase tracking-[0.3em] text-gray-400">Clero</p>
            <h1 className="text-2xl font-black tracking-tight">Something went wrong.</h1>
            <p className="text-gray-600 font-medium">Refresh the page and try again.</p>
          </div>
        </div>
      )}
      showDialog={false}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}
